import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { Plus, Edit, Trash2, Loader2, Brain, ArrowLeft, ChevronUp, ChevronDown } from 'lucide-react';

const ManagePersonalityQuestions = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [formData, setFormData] = useState({ question_text: '', metric_mapping: 'loss_aversion', reverse_scored: false, display_order: 0, scoring_weight: 1.00 });
  const [errors, setErrors] = useState({});
  const [reordering, setReordering] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/questions/personality');
      setQuestions(response.data);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.question_text.trim()) newErrors.question_text = 'Question text is required';
    if (!formData.metric_mapping) newErrors.metric_mapping = 'Metric mapping is required';
    if (isNaN(parseFloat(formData.scoring_weight)) || parseFloat(formData.scoring_weight) <= 0) newErrors.scoring_weight = 'Scoring weight must be a positive number';
    if (isNaN(parseInt(formData.display_order)) || parseInt(formData.display_order) < 0) newErrors.display_order = 'Display order must be a non-negative integer';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveQuestion = async () => {
    if (!validateForm()) return;

    try {
      if (currentQuestion) {
        await api.put(`/questions/personality/${currentQuestion.id}`, formData);
      } else {
        await api.post('/questions/personality', { ...formData, scoring_weight: parseFloat(formData.scoring_weight), display_order: parseInt(formData.display_order) });
      }
      setModalOpen(false);
      fetchQuestions();
    } catch (error) {
      console.error('Failed to save question:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to save question' });
    }
  };

  const handleEditQuestion = (question) => {
    setCurrentQuestion(question);
    setFormData({
      question_text: question.question_text,
      metric_mapping: question.metric_mapping,
      reverse_scored: question.reverse_scored,
      display_order: question.display_order,
      scoring_weight: question.scoring_weight
    });
    setErrors({});
    setModalOpen(true);
  };

  const handleNewQuestion = () => {
    setCurrentQuestion(null);
    setFormData({ question_text: '', metric_mapping: 'loss_aversion', reverse_scored: false, display_order: questions.length, scoring_weight: 1.00 });
    setErrors({});
    setModalOpen(true);
  };

  // Move a question one position up (direction -1) or down (direction +1).
  // Swaps display_order with the neighbour and persists both via the existing
  // per-question endpoint. Optimistic UI update; reverts by refetching on error.
  const handleMoveQuestion = async (index, direction) => {
    if (reordering) return;
    const target = index + direction;
    if (target < 0 || target >= questions.length) return;

    const a = questions[index];
    const b = questions[target];
    // If two neighbours share the same order value, give them distinct ones so
    // the swap actually changes the sort.
    const orderA = a.display_order === b.display_order ? index : a.display_order;
    const orderB = a.display_order === b.display_order ? target : b.display_order;

    setReordering(true);
    // Optimistic reorder
    const optimistic = [...questions];
    optimistic[index] = { ...b, display_order: orderA };
    optimistic[target] = { ...a, display_order: orderB };
    setQuestions(optimistic);

    try {
      await Promise.all([
        api.put(`/questions/personality/${a.id}`, { ...a, display_order: orderB }),
        api.put(`/questions/personality/${b.id}`, { ...b, display_order: orderA }),
      ]);
      await fetchQuestions();
    } catch (error) {
      console.error('Failed to reorder questions:', error);
      await fetchQuestions(); // revert to server truth
    } finally {
      setReordering(false);
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    try {
      await api.delete(`/questions/personality/${id}`);
      fetchQuestions();
    } catch (error) {
      console.error('Failed to delete question:', error);
    }
  };

  const metricOptions = [
    { value: 'loss_aversion', label: 'Loss Aversion' },
    { value: 'gain_attraction', label: 'Gain Attraction' },
    { value: 'volatility_anxiety', label: 'Volatility Anxiety' },
    { value: 'intervention_risk', label: 'Intervention Risk' }
  ];

  // Per-metric summary, derived live from the current questions. Scoring is
  // dynamic: each metric is scored out of (6 × number of its questions), so
  // adding or removing questions here automatically rescales the maths.
  const MAX_PER_QUESTION = 6;
  const metricSummary = metricOptions.map((m) => {
    const count = questions.filter((q) => q.metric_mapping === m.value).length;
    return { ...m, count, maxScore: count * MAX_PER_QUESTION };
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>Manage Personality Questions</h1>
            </div>
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin Panel
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {!loading && questions.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {metricSummary.map((m) => (
            <Card key={m.value}>
              <CardContent>
                <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: theme.textSecondary }}>{m.label}</p>
                <p className="text-2xl font-bold" style={{ color: m.count === 0 ? '#ef4444' : theme.text }}>
                  {m.count} <span className="text-sm font-normal" style={{ color: theme.textSecondary }}>question{m.count === 1 ? '' : 's'}</span>
                </p>
                <p className="text-xs mt-1" style={{ color: theme.textSecondary }}>
                  {m.count === 0 ? 'Not scored — add a question' : `Scored out of ${m.maxScore} (6 × ${m.count})`}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm" style={{ color: theme.textSecondary }}>
              Scoring is dynamic — each metric is scored out of 6 × its question count, so you can add or remove questions freely.
            </p>
            <Button onClick={handleNewQuestion}>
              <Plus className="w-5 h-5 mr-2" /> Add Question
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-20">
              <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: theme.primary }} />
              <p className="text-lg" style={{ color: theme.textSecondary }}>Loading questions...</p>
            </div>
          ) : questions.length === 0 ? (
            <div className="text-center py-12">
              <Brain className="w-16 h-16 mx-auto mb-4 opacity-20" style={{ color: theme.text }} />
              <p className="text-lg" style={{ color: theme.textSecondary }}>No questions found</p>
            </div>
          ) : (
            <table className="min-w-full divide-y" style={{ borderColor: theme.border }}>
              <thead>
                <tr>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>ID</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Question</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Metric</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Reverse</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Order</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Weight</th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium uppercase tracking-wider" style={{ color: theme.textSecondary }}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: theme.border }}>
                {questions.map((q, index) => (
                  <tr key={q.id}>
                    <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.text }}>{q.id}</td>
                    <td className="py-3 px-4 text-sm min-w-[300px] break-words" style={{ color: theme.text }}>
                      <span title={q.question_text} >{q.question_text}</span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm capitalize" style={{ color: theme.text }}>{q.metric_mapping}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.text }}>{q.reverse_scored ? 'Yes' : 'No'}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.text }}>{q.display_order}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm" style={{ color: theme.text }}>{q.scoring_weight}</td>
                    <td className="py-3 px-4 whitespace-nowrap text-sm space-x-1">
                      <button
                        onClick={() => handleMoveQuestion(index, -1)}
                        disabled={index === 0 || reordering}
                        title="Move up"
                        className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronUp className="w-4 h-4 inline-block" />
                      </button>
                      <button
                        onClick={() => handleMoveQuestion(index, 1)}
                        disabled={index === questions.length - 1 || reordering}
                        title="Move down"
                        className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronDown className="w-4 h-4 inline-block" />
                      </button>
                      <button onClick={() => handleEditQuestion(q)} title="Edit" className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                        <Edit className="w-4 h-4 inline-block" />
                      </button>
                      <button onClick={() => handleDeleteQuestion(q.id)} title="Delete" className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                        <Trash2 className="w-4 h-4 inline-block" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      </main>

      {/* Modal for Add/Edit Question */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="max-w-2xl w-full">
            <Card>
              <CardHeader>
                <h2 className="text-2xl font-bold" style={{ color: theme.text }}>{currentQuestion ? 'Edit Question' : 'Add New Question'}</h2>
              </CardHeader>
              <CardContent>
                <form onSubmit={(e) => { e.preventDefault(); handleSaveQuestion(); }} noValidate className="space-y-4">
                  {errors.submit && <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>}

                  <Input label="Question Text" value={formData.question_text} onChange={(e) => setFormData({ ...formData, question_text: e.target.value })} error={errors.question_text} required rows={3} as="textarea" />
                  
                  <Select label="Metric Mapping" options={metricOptions} value={formData.metric_mapping} onChange={(e) => setFormData({ ...formData, metric_mapping: e.target.value })} error={errors.metric_mapping} required />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                       <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reverse Scored</label>
                       <input
                         type="checkbox"
                         checked={formData.reverse_scored}
                         onChange={(e) => setFormData({ ...formData, reverse_scored: e.target.checked })}
                         className="mt-1 w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                       />
                    </div>
                    <Input label="Display Order" type="number" value={formData.display_order} onChange={(e) => setFormData({ ...formData, display_order: e.target.value })} error={errors.display_order} required />
                  </div>
                  
                  <Input label="Scoring Weight" type="number" step="0.01" value={formData.scoring_weight} onChange={(e) => setFormData({ ...formData, scoring_weight: e.target.value })} error={errors.scoring_weight} required />

                  <div className="pt-4 flex justify-end space-x-3">
                    <Button type="button" variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
                    <Button type="submit">Save Question</Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagePersonalityQuestions;
